import type {
  FindingCenterListItem as ServerFindingCenterListItem,
  FindingCenterListResponse,
} from '@kop/inspection-firebase-adapter';

import {
  INSPECTION_DATA_CHANGED_EVENT,
  notifyInspectionDataChanged,
} from '../../runtime/inspection-data-events';
import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { mergeFindingCenterPreset } from './finding-center-model';
import type {
  FindingCenterAllowedAction,
  FindingCenterDetail,
  FindingCenterEvidence,
  FindingCenterFilter,
  FindingCenterListItem,
  FindingCenterPage,
  FindingCenterReadPort,
  FindingCenterStatus,
} from './finding-center-model';

type Environment = Readonly<Record<string, string | boolean | undefined>>;
type Timed<T> = Readonly<{ value: T; readAt: number }>;

const LIST_FRESH_MS = 30_000;
const DETAIL_FRESH_MS = 2 * 60_000;

class DisabledFindingCenterReadPort implements FindingCenterReadPort {
  readonly enabled = false;
  async list(): Promise<never> {
    throw new Error('FINDING_CENTER_DISABLED');
  }
  async detail(): Promise<never> {
    throw new Error('FINDING_CENTER_DISABLED');
  }
  async deleteFinding(): Promise<never> {
    throw new Error('FINDING_CENTER_DISABLED');
  }
  async correctAssignment(): Promise<never> {
    throw new Error('FINDING_CENTER_DISABLED');
  }
}

function statusOf(item: ServerFindingCenterListItem): Exclude<FindingCenterStatus, 'all'> {
  if (item.reassignNeeded) return 'reassign-needed';
  if (item.status === 'completed') return 'complete';
  if (item.status === 'approval-pending') return 'approval-pending';
  return 'unresolved';
}

function statusLabel(status: Exclude<FindingCenterStatus, 'all'>) {
  return {
    unresolved: '미조치',
    'approval-pending': '검토 중',
    complete: '조치완료',
    'reassign-needed': '업체 지정 필요',
  }[status];
}

export function inspectionKindLabel(kind: string) {
  return (
    {
      routine: '안전보건점검',
      structured: '안전보건점검',
      joint: '합동점검',
      weekly: '주간점검',
      patrol: '순회점검',
      night: '야간점검',
      special: '특별안전보건점검',
      quick: '수시점검',
    }[kind] ?? '점검'
  );
}

export function koreanSnapshotLabel(value: string | null | undefined, fallback: string) {
  const normalized = String(value ?? '')
    .normalize('NFKC')
    .trim();
  return /[가-힣]/u.test(normalized) ? normalized : fallback;
}

export function userFacingSnapshotLabel(value: string | null | undefined, fallback: string) {
  const normalized = String(value ?? '')
    .normalize('NFKC')
    .trim();
  if (!normalized) return fallback;
  if (/^[a-f\d]{8}-[a-f\d-]{27,}$/iu.test(normalized)) return fallback;
  if (
    /^(contractor|identity|person|site|trade|location|finding|inspection)[-_:]/iu.test(normalized)
  )
    return fallback;
  return normalized;
}

function evidenceOf(
  evidence: ServerFindingCenterListItem['firstBeforeEvidence'],
  variant: FindingCenterEvidence['variant'],
): FindingCenterEvidence | null {
  return evidence
    ? {
        manifestId: evidence.manifestId,
        sha256: variant === 'thumbnail' ? evidence.thumbRef.sha256 : evidence.detailRef.sha256,
        variant,
        fallbackSha256: variant === 'thumbnail' ? evidence.detailRef.sha256 : undefined,
      }
    : null;
}

function overdueDays(dueAt: string | null, status: FindingCenterListItem['status']) {
  if (!dueAt || status === 'complete') return 0;
  const due = new Date(dueAt);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86_400_000));
}

function listItem(item: ServerFindingCenterListItem): FindingCenterListItem {
  const status = statusOf(item);
  return {
    findingId: item.findingId,
    version: item.version,
    status,
    statusLabel: statusLabel(status),
    summary: userFacingSnapshotLabel(item.checklistItemLabel, '점검 지적사항'),
    description: userFacingSnapshotLabel(item.summary, '세부 내용을 확인해 주세요.'),
    inspectionKind: item.inspectionKind,
    inspectionKindLabel: inspectionKindLabel(item.inspectionKind),
    workTradeId: item.workTrade?.id ?? '',
    workTradeLabel: koreanSnapshotLabel(item.workTrade?.label, '공종 미지정'),
    workLocationId: item.workLocation?.id ?? '',
    workLocationLabel: userFacingSnapshotLabel(item.workLocation?.label, '작업장소 미지정'),
    contractorId: item.contractor.id,
    contractorLabel: userFacingSnapshotLabel(item.contractor.label, '담당 업체 미지정'),
    authorName: userFacingSnapshotLabel(item.author.name, '작성자 미확인'),
    occurredAt: item.createdAt,
    dueAt: item.dueAt,
    overdueDays: overdueDays(item.dueAt, status),
    thumbnail: evidenceOf(item.firstBeforeEvidence, 'thumbnail'),
  };
}

function queryOf(filter: FindingCenterFilter) {
  return {
    tenantId: filter.tenantId,
    siteId: filter.siteId,
    status: filter.status,
    period: filter.period ?? null,
    inspectionKind: filter.inspectionKind ?? null,
    checklistItemId: filter.checklistItemId ?? null,
    resolutionMode: filter.resolutionMode ?? null,
    quality: filter.quality ?? null,
    workTradeId: filter.workTradeId ?? null,
    workLocationId: filter.workLocationId ?? null,
    contractorId: filter.contractorId ?? null,
    authorUid: null,
    search: filter.search ?? null,
    pageSize: filter.pageSize,
    cursor: filter.cursor ?? null,
  } as const;
}

export function findingCenterDetailCacheKey(input: { siteId: string; findingId: string }) {
  return `${input.siteId}:${input.findingId}`;
}

function pageOf(
  response: FindingCenterListResponse,
  filter: FindingCenterFilter,
): FindingCenterPage {
  return {
    items: response.items.map(listItem),
    total: response.total,
    nextCursor: response.nextCursor,
    statusCounts: response.statusCounts,
    completeness: {
      ...response.completeness,
      reason: response.completeness.reason ?? undefined,
      authoritativeCount: response.completeness.authoritativeCount ?? response.total,
    },
    appliedFilter: filter,
    options: {
      workTrades: response.filterOptions.workTrades.map((trade) => ({
        id: trade.id,
        label: userFacingSnapshotLabel(trade.label, '공종 미지정'),
        workLocations: trade.locations.map((location) => ({
          ...location,
          label: userFacingSnapshotLabel(location.label, '작업장소 미지정'),
        })),
      })),
      contractors: response.filterOptions.contractors,
      inspectionKinds: response.filterOptions.inspectionKinds.map((kind) => ({
        ...kind,
        label: inspectionKindLabel(kind.id),
      })),
    },
  };
}

export function createFindingCenterReadPort(
  environment: Environment = import.meta.env,
): FindingCenterReadPort {
  const mode =
    environment.VITE_INSPECTION_DATA_ADAPTER ??
    (environment.VITE_AUTH_ADAPTER === 'firebase' ? 'firebase' : undefined);
  if (mode !== 'firebase' && mode !== 'firebase-emulator') {
    return new DisabledFindingCenterReadPort();
  }
  const projectId = String(environment.VITE_FIREBASE_PROJECT_ID ?? '');
  if (mode === 'firebase' && projectId !== 'kop-safety-51efb') {
    throw new Error('Finding center production adapter requires the corporate Firebase project');
  }
  if (mode === 'firebase-emulator' && !projectId.startsWith('demo-')) {
    throw new Error('Finding center emulator adapter requires a demo-* project ID');
  }
  const server = import('@kop/inspection-firebase-adapter').then((module) =>
    module.createInspectionFindingCenterReadPort(projectId),
  );
  const listCache = createSessionKeyedReadCache<Timed<FindingCenterPage>>(10);
  const detailCache = createSessionKeyedReadCache<Timed<FindingCenterDetail>>(16);
  const listKey = (input: FindingCenterFilter) => JSON.stringify(queryOf(input));
  const invalidate = () => {
    listCache.invalidate();
    detailCache.invalidate();
  };
  return {
    enabled: true,
    async list(input, options) {
      const key = listKey(input);
      const cached = listCache.peek(key);
      const force =
        options?.force === true || Boolean(cached && Date.now() - cached.readAt > LIST_FRESH_MS);
      const entry = await listCache.read(
        key,
        async () => ({
          value: pageOf(
            await (await server).list({ ...queryOf(input), ...(force ? { fresh: true } : {}) }),
            input,
          ),
          readAt: Date.now(),
        }),
        force,
      );
      return entry.value;
    },
    peekList(input) {
      return listCache.peek(listKey(input))?.value ?? null;
    },
    async detail(input, options) {
      const detailKey = findingCenterDetailCacheKey(input);
      const cached = detailCache.peek(detailKey);
      const force =
        options?.force === true ||
        Boolean(cached && Date.now() - cached.readAt > DETAIL_FRESH_MS) ||
        Boolean(
          cached &&
          input.expectedVersion !== undefined &&
          cached.value.item.version !== input.expectedVersion,
        );
      const entry = await detailCache.read(
        detailKey,
        async () => {
          const response = await (await server).detail({
            tenantId: input.tenantId,
            siteId: input.siteId,
            findingId: input.findingId,
          });
          const item = listItem(response.item);
          return {
            value: {
              item,
              beforeEvidence: response.beforeEvidence.map((evidence) =>
                evidenceOf(evidence, 'detail')!,
              ),
              afterEvidence: response.afterEvidence.map((evidence) =>
                evidenceOf(evidence, 'detail')!,
              ),
              history: response.history,
              allowedActions: response.allowedActions as readonly FindingCenterAllowedAction[],
            } satisfies FindingCenterDetail,
            readAt: Date.now(),
          };
        },
        force,
      );
      return entry.value;
    },
    async deleteFinding(input) {
      const response = await (
        await server
      ).deleteFinding({
        tenantId: 'kop',
        ...input,
      });
      if (response.status !== 'succeeded' || response.commandId !== input.commandId)
        throw new Error('FINDING_DELETE_RECEIPT_INVALID');
      invalidate();
      notifyInspectionDataChanged();
    },
    async correctAssignment(input) {
      const response = await (
        await server
      ).correctAssignment({
        tenantId: 'kop',
        ...input,
      });
      if (response.status !== 'succeeded' || response.commandId !== input.commandId)
        throw new Error('ASSIGNMENT_CORRECTION_RECEIPT_INVALID');
      invalidate();
      notifyInspectionDataChanged();
    },
    invalidate,
  };
}

let findingCenterPortHolder: { value: FindingCenterReadPort } | null = null;

export function getFindingCenterReadPort() {
  findingCenterPortHolder ??= { value: createFindingCenterReadPort() };
  return findingCenterPortHolder.value;
}

export async function prefetchFindingCenter(siteId: string): Promise<void> {
  await getFindingCenterReadPort().list(mergeFindingCenterPreset(undefined, siteId));
}

if (typeof window !== 'undefined') {
  window.addEventListener(INSPECTION_DATA_CHANGED_EVENT, () =>
    findingCenterPortHolder?.value.invalidate?.(),
  );
}
